import { Button, Divider, Form, Input, message, Modal } from "antd";
import * as API from "common/api";
import * as COMMONS from "common/common";
import { TapAnimationComponent } from "components";
import { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import socket from "libs/socket-io";

const { TextArea } = Input;

const AdminNotificationModal = (props) => {
	const {
		publicSettings,
		isAdminNotificationModalVisible,
		hideAdminNotificationModal,
	} = props;

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [adminNotificationForm] = Form.useForm();

	const adminNotificationMutation = useMutation(
		API.ADMIN_UPDATE_SETTINGS_BATCH,
		{
			onSuccess: (response) => {
				if (isMountedRef.current) {
					message.success(COMMONS.SUCCESS_UPDATE_MSG);
					queryClient.invalidateQueries({
						queryKey: [API.QUERY_KEY_ADMIN_PUBLIC_SETTINGS],
					});
					hideAdminNotificationModal();
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	useEffect(() => {
		if (!isAdminNotificationModalVisible) {
			adminNotificationForm.resetFields();
		}
	}, [isAdminNotificationModalVisible, adminNotificationForm]);

	useEffect(() => {
		if (isAdminNotificationModalVisible) {
			adminNotificationForm.setFieldsValue({
				memberMessage: publicSettings?.ADMIN_MESSAGE_MEMBER || undefined,
				campaignMessage: publicSettings?.ADMIN_MESSAGE_CAMPAIGN || undefined,
				reservationMessage:
					publicSettings?.ADMIN_MESSAGE_RESERVATION || undefined,
				reservationCancelMessage:
					publicSettings?.ADMIN_MESSAGE_RESERVATION_CANCEL || undefined,
			});
		}

		// eslint-disable-next-line
	}, [isAdminNotificationModalVisible]);

	useEffect(() => {
		socket.on(API.SOCKET_SYSTEM_SETTING, () => {
			queryClient.invalidateQueries({
				queryKey: [API.QUERY_KEY_ADMIN_PUBLIC_SETTINGS],
			});
		});

		return () => {
			socket.off(API.SOCKET_SYSTEM_SETTING);

			socket.disconnect();
		};

		// eslint-disable-next-line
	}, []);

	const handleAdminNotification = (data) => {
		let paramArray = [];

		paramArray.push({
			name: API.SETTINGS_KEY_ADMIN_MESSAGE_MEMBER,
			label: API.SETTINGS_LABEL_ADMIN_MESSAGE_MEMBER,
			valueString: data.memberMessage,
			isPublic: false,
		});

		paramArray.push({
			name: API.SETTINGS_KEY_ADMIN_MESSAGE_CAMPAIGN,
			label: API.SETTINGS_LABEL_ADMIN_MESSAGE_CAMPAIGN,
			valueString: data.campaignMessage,
			isPublic: false,
		});

		paramArray.push({
			name: API.SETTINGS_KEY_ADMIN_MESSAGE_RESERVATION,
			label: API.SETTINGS_LABEL_ADMIN_MESSAGE_RESERVATION,
			valueString: data.reservationMessage,
			isPublic: false,
		});

		paramArray.push({
			name: API.SETTINGS_KEY_ADMIN_MESSAGE_RESERVATION_CANCEL,
			label: API.SETTINGS_LABEL_ADMIN_MESSAGE_RESERVATION_CANCEL,
			valueString: data.reservationCancelMessage,
			isPublic: false,
		});

		const paramData = {
			settings: paramArray,
		};

		adminNotificationMutation.mutate(paramData);
	};

	return (
		<>
			<Modal
				open={isAdminNotificationModalVisible}
				onCancel={hideAdminNotificationModal}
				title="管理者通知設定"
				footer={null}
				styles={{
					body: {
						maxHeight: "90vh",
						overflowY: "auto",
						overflowX: "hidden",
					},
				}}
				maskClosable={false}
				width={720}
				destroyOnClose
				centered
			>
				<Form
					form={adminNotificationForm}
					layout="vertical"
					initialValues={{
						memberMessage: undefined,
						campaignMessage: undefined,
						reservationMessage: undefined,
						reservationCancelMessage: undefined,
					}}
					onFinish={handleAdminNotification}
					size="large"
					requiredMark={false}
					scrollToFirstError={COMMONS.FORM_SCROLL_CONFIG}
				>
					<motion.div
						variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
						initial="hidden"
						animate="show"
						exit="hidden"
					>
						<motion.div
							variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
							className="p-4 bg-yellow-300 rounded mb-8"
						>
							<span className="text-center">
								設定されてない場合は、メッセージが配信されません。
							</span>
						</motion.div>
						<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
							<Form.Item
								name="memberMessage"
								label="新規メンバーが登録されると配信されるメッセージ"
								extra={
									<span className="whitespace-pre-wrap">{`※メッセージに[NAME]を入力すると、配信の時にお客様の名前に変換されます。\n※メッセージに[TEL]を入力すると、配信の時にお客様の電話番号に変換されます。`}</span>
								}
							>
								<TextArea placeholder="メッセージを入力してください" autoSize />
							</Form.Item>
						</motion.div>
						<Divider />
						<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
							<Form.Item
								name="reservationMessage"
								label="新規予約が登録されると送信されるメッセージ"
								extra={
									<span className="whitespace-pre-wrap">{`※メッセージに[BUILDING]を入力すると、配信の時に予約の${COMMONS.DEFAULT_SYSTEM_TYPE}名に変換されます。\n※メッセージに[NAME]を入力すると、配信の時に予約者の名前に変換されます。\n※メッセージに[DATE]を入力すると、配信の時に予約の日付に変換されます。\n※メッセージに[TEL]を入力すると、配信の時に予約者の電話番号に変換されます。`}</span>
								}
							>
								<TextArea placeholder="メッセージを入力してください" autoSize />
							</Form.Item>
						</motion.div>
						<Divider />
						<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
							<Form.Item
								name="reservationCancelMessage"
								label="予約がキャンセルされると送信されるメッセージ"
								extra={
									<span className="whitespace-pre-wrap">{`※メッセージに[BUILDING]を入力すると、配信の時に予約の${COMMONS.DEFAULT_SYSTEM_TYPE}名に変換されます。\n※メッセージに[NAME]を入力すると、配信の時に予約者の名前に変換されます。\n※メッセージに[DATE]を入力すると、配信の時に予約の日付に変換されます。\n※メッセージに[TEL]を入力すると、配信の時に予約者の電話番号に変換されます。`}</span>
								}
							>
								<TextArea placeholder="メッセージを入力してください" autoSize />
							</Form.Item>
						</motion.div>
						<Divider />
						<motion.div
							variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
							className="flex justify-center"
						>
							<TapAnimationComponent>
								<Button
									type="primary"
									htmlType="submit"
									size="large"
									className="m-1 w-32"
									loading={adminNotificationMutation.isLoading}
								>
									保存する
								</Button>
							</TapAnimationComponent>
						</motion.div>
					</motion.div>
				</Form>
			</Modal>
		</>
	);
};

export default AdminNotificationModal;
